import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Music, Zap, BarChart3, Headphones, Upload, Cloud, Sparkles, Monitor, Download, CheckCircle2 } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import BrandLogo from '@/components/BrandLogo';

export default function HomePage() {
  useDocumentTitle('AI DJ Software - Professional Track Analyzer & Harmonic Mixing Tool | CRATELAB');
  const { isAuthenticated, user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isFirstLogin = location.state?.firstLogin === true;

  // Redirect logged-in users to library (unless first login or admin)
  useEffect(() => {
    if (!loading && isAuthenticated && user?.role !== 'admin' && !isFirstLogin) {
      navigate('/library', { replace: true });
    }
  }, [loading, isAuthenticated, user, navigate, isFirstLogin]);

  const features = [
    { icon: Upload, title: 'Web Uploads', desc: 'Upload tracks directly' },
    { icon: BarChart3, title: 'BPM Detection', desc: 'Precise tempo analysis' },
    { icon: Music, title: 'Camelot Wheel', desc: 'Key compatibility mapping' },
    { icon: Cloud, title: 'Cloud Library', desc: 'Access anywhere, anytime' },
  ];

  // Camelot wheel data with colors
  const camelotKeys = [
    { num: 1, color: '#FF6B6B' },
    { num: 2, color: '#FFA94D' },
    { num: 3, color: '#FFD43B' },
    { num: 4, color: '#A9E34B' },
    { num: 5, color: '#69DB7C' },
    { num: 6, color: '#38D9A9' },
    { num: 7, color: '#3BC9DB' },
    { num: 8, color: '#4DABF7' },
    { num: 9, color: '#748FFC' },
    { num: 10, color: '#9775FA' },
    { num: 11, color: '#DA77F2' },
    { num: 12, color: '#F783AC' },
  ];

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#2563FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-0 sm:min-h-[90vh] flex flex-col lg:flex-row lg:items-center overflow-hidden bg-white">
        {/* Soft radial glow stack behind everything */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-32 -left-32 w-[600px] h-[600px] rounded-full bg-[#2563FF]/12 blur-[140px]" />
          <div className="absolute bottom-0 right-0 w-[700px] h-[700px] rounded-full bg-[#14E6C9]/14 blur-[160px]" />
          <div className="absolute top-1/2 right-1/4 w-[400px] h-[400px] rounded-full bg-[#22D3EE]/10 blur-[120px]" />
        </div>

        {/* Subtle grid */}
        <div className="absolute inset-0 opacity-[0.3] pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(rgba(37, 99, 255, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(37, 99, 255, 0.08) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
            maskImage: 'radial-gradient(ellipse 70% 60% at 30% 50%, black 30%, transparent 80%)',
            WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 30% 50%, black 30%, transparent 80%)',
          }}
        />

        {/* Mobile-only full-bleed droid header — covers the entire top of the hero */}
        <div className="lg:hidden relative w-full overflow-hidden" style={{ aspectRatio: '4 / 5' }}>
          <img
            src="/hero-droid.png"
            alt=""
            aria-hidden="true"
            draggable={false}
            className="absolute inset-0 w-full h-full object-cover object-top scale-110"
            style={{
              filter: 'drop-shadow(0 18px 36px rgba(37, 99, 255, 0.22)) drop-shadow(0 0 30px rgba(34, 211, 238, 0.16))',
            }}
          />
          {/* Soft bottom fade so the droid blends into the page */}
          <div
            className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
            style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.95) 85%, #FFFFFF 100%)',
            }}
          />
        </div>

        {/* Droid image — absolute on the right for desktop only */}
        <div className="hidden lg:block absolute inset-y-0 right-0 w-[58%] pointer-events-none">
          {/* Blue spotlight halo */}
          <div className="absolute top-1/2 right-[10%] -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-[#22D3EE]/22 via-[#2563FF]/12 to-transparent blur-[100px]" />
          <div className="absolute top-1/3 right-[20%] w-[320px] h-[320px] rounded-full bg-[#14E6C9]/18 blur-[120px]" />
          {/* Droid */}
          <div
            className="absolute inset-y-0 right-0 w-full bg-no-repeat bg-contain bg-right"
            style={{
              backgroundImage: 'url(/hero-droid.png)',
              filter: 'drop-shadow(0 30px 80px rgba(37, 99, 255, 0.18)) drop-shadow(0 0 50px rgba(34, 211, 238, 0.12))',
              maskImage: 'linear-gradient(90deg, transparent 0%, black 18%, black 100%)',
              WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, black 18%, black 100%)',
            }}
          />
          {/* Extra soft fade-to-page on the left edge */}
          <div
            className="absolute inset-y-0 left-0 w-1/3 pointer-events-none"
            style={{
              background: 'linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.45) 55%, rgba(255,255,255,0) 100%)',
            }}
          />
        </div>

        {/* Text column */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 w-full mt-6 lg:mt-0 pb-4 lg:pb-0">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Eyebrow pill */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 sm:mb-8 rounded-full bg-white/80 backdrop-blur-md border border-[#E2E8F0] shadow-sm"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#14E6C9] animate-pulse" />
                <span className="font-mono text-xs text-[#475569] tracking-wide">AI-POWERED DJ INTELLIGENCE</span>
              </motion.div>

              <p className="font-body text-lg sm:text-xl text-[#475569] max-w-xl mb-6 sm:mb-10 leading-relaxed">
                AI-powered DJ software that analyzes BPM, key, and energy to create perfectly harmonic mixes. CRATELAB reads your library,
                scores transitions and builds sets that sound like <em className="not-italic text-[#0F172A]">you</em> — only sharper.
              </p>

              {/* Trust strip — mobile only, sits above the Sign In button */}
              <div className="sm:hidden mb-6 flex items-center gap-4 text-[11px] font-mono text-[#94A3B8]">
                <span className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[#14E6C9]" /> NATIVE BPM + KEY</span>
                <span className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[#2563FF]" /> HARMONIC SCORING</span>
              </div>

              <div className="hidden sm:flex flex-col sm:flex-row gap-4">
                <Link to="/login" data-testid="hero-login-btn">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
                  >
                    Sign In
                    <ArrowRight className="w-5 h-5" />
                  </motion.button>
                </Link>
              
              </div>

              {/* Trust strip — desktop only, sits below the buttons */}
              <div className="hidden sm:flex mt-12 items-center gap-6 text-xs font-mono text-[#94A3B8]">
                <span className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[#14E6C9]" /> NATIVE BPM + KEY</span>
                <span className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[#2563FF]" /> HARMONIC SCORING</span>
                <span className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[#22D3EE]" /> OFFLINE READY</span>
              </div>
            </motion.div>
          </div>
        </div>

        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="hidden sm:block absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        >
          <div className="w-6 h-10 border-2 border-[#2563FF]/40 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 gradient-primary rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Dashboard Preview Section */}
      <section className="relative py-10 sm:py-24 px-4 border-t border-[#E2E8F0] overflow-hidden">
        {/* Ambient gradient backdrop */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-[#2563FF]/8 blur-[140px]" />
          <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] rounded-full bg-[#14E6C9]/10 blur-[160px]" />
        </div>

        <div className="relative max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mb-16"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-5 rounded-full bg-[#EFF6FF] border border-[#DBEAFE]">
              <span className="font-mono text-[10px] text-[#2563FF] tracking-widest">LIVE COCKPIT</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight leading-[1.05]">
              Every signal you need to <span className="gradient-text">build the perfect set.</span>
            </h2>
            <p className="font-body text-[#475569] text-lg mt-5 max-w-xl">
              CRATELAB surfaces harmonic mixes, energy flow and transition scoring in one calm, glassy workspace.
              Our AI DJ assistant analyzes your entire library to suggest compatible tracks using the Camelot wheel system —
              so you stop second-guessing and start pre-listening.
            </p>
          </motion.div>

          {/* 2x2 glassmorphic dashboard grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            {/* AI Set Builder — large */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-7 relative rounded-2xl border border-white/60 bg-white/70 backdrop-blur-xl p-7 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.15)] overflow-hidden"
            >
              <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#22D3EE]/15 blur-3xl pointer-events-none" />
              <div className="flex items-center justify-between mb-5 relative">
                <div>
                  <p className="font-mono text-[10px] text-[#94A3B8] tracking-widest mb-1">AI SET BUILDER</p>
                  <h3 className="font-heading text-xl font-semibold text-[#0F172A]">Techno Peak Time Set</h3>
                </div>
                <div className="flex items-center gap-2">
                  {['Techno', '128 BPM', '60 Tracks', '1h 32m'].map(t => (
                    <span key={t} className="hidden sm:inline-flex px-2.5 py-1 rounded-full bg-[#EFF6FF] border border-[#DBEAFE] font-mono text-[10px] text-[#2563FF] tracking-wide">{t}</span>
                  ))}
                </div>
              </div>
              {/* Waveform */}
              <div className="relative h-24 rounded-xl bg-gradient-to-br from-[#F8FAFC] to-white border border-[#E2E8F0] overflow-hidden mb-5">
                <svg viewBox="0 0 800 100" preserveAspectRatio="none" className="w-full h-full">
                  <defs>
                    <linearGradient id="hpWave" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#2563FF" />
                      <stop offset="100%" stopColor="#14E6C9" />
                    </linearGradient>
                  </defs>
                  {Array.from({ length: 100 }).map((_, i) => {
                    const h = 18 + Math.abs(Math.sin(i * 0.4) * 28) + Math.abs(Math.cos(i * 0.18) * 12);
                    return <rect key={i} x={i * 8} y={50 - h/2} width="3" height={h} rx="1.5" fill="url(#hpWave)" opacity={0.55 + Math.sin(i*0.3)*0.25} />;
                  })}
                </svg>
              </div>
              {/* Mini track table */}
              <div className="space-y-2">
                {[
                  { i:1, t:'Eclipse', a:'ANNA', bpm:126, key:'8A', e:72 },
                  { i:2, t:'The Cycle', a:'Kevin de Vries', bpm:127, key:'9A', e:76 },
                  { i:3, t:'Hidden Places', a:'Adam Beyer', bpm:129, key:'10A', e:84 },
                  { i:4, t:'Rave Signal', a:'Dax J', bpm:130, key:'10B', e:88 },
                ].map(r => (
                  <div key={r.i} className="grid grid-cols-12 items-center gap-3 py-2 px-3 rounded-lg hover:bg-white/80 transition-colors">
                    <span className="col-span-1 font-mono text-xs text-[#94A3B8]">{String(r.i).padStart(2,'0')}</span>
                    <div className="col-span-5">
                      <p className="font-heading text-sm text-[#0F172A] truncate">{r.t}</p>
                      <p className="font-body text-xs text-[#64748B] truncate">{r.a}</p>
                    </div>
                    <span className="col-span-2 font-mono text-xs text-[#0F172A] text-center">{r.bpm}</span>
                    <span className="col-span-1 font-mono text-xs font-bold text-[#2563FF] text-center">{r.key}</span>
                    <div className="col-span-3 flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-[#F1F5F9] overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-[#2563FF] to-[#14E6C9]" style={{ width: `${r.e}%` }} />
                      </div>
                      <span className="font-mono text-[10px] text-[#64748B] w-7">{(r.e/10).toFixed(1)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* AI Assistant chat */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-5 relative rounded-2xl border border-white/60 bg-white/70 backdrop-blur-xl p-7 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.15)] overflow-hidden"
            >
              <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-[#2563FF]/15 blur-3xl pointer-events-none" />
              <div className="flex items-center justify-between mb-5 relative">
                <div>
                  <p className="font-mono text-[10px] text-[#94A3B8] tracking-widest mb-1">AI ASSISTANT</p>
                  <h3 className="font-heading text-xl font-semibold text-[#0F172A]">Hi Alex</h3>
                  <p className="font-body text-sm text-[#64748B]">How can I help you build the perfect set?</p>
                </div>
                <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center neon-glow">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
              </div>
              <div className="space-y-3 relative">
                <div className="ml-auto max-w-[85%] rounded-2xl rounded-tr-sm bg-gradient-to-br from-[#EFF6FF] to-[#ECFEFF] border border-[#DBEAFE] px-4 py-2.5">
                  <p className="font-body text-sm text-[#0F172A]">I want a high-energy techno peak time set, 90 minutes.</p>
                </div>
                <div className="mr-auto max-w-[90%] rounded-2xl rounded-tl-sm bg-white border border-[#E2E8F0] px-4 py-2.5 shadow-sm">
                  <p className="font-body text-sm text-[#0F172A]">Got it. I'll seed at <span className="font-mono text-[#2563FF]">126 BPM / 8A</span> and build to <span className="font-mono text-[#2563FF]">131 / 11B</span> across 16 tracks.</p>
                </div>
                <div className="mr-auto max-w-[90%] rounded-2xl rounded-tl-sm bg-white border border-[#E2E8F0] px-4 py-2.5 shadow-sm">
                  <p className="font-body text-sm text-[#0F172A]">Drop a reset around 60 minutes — want me to slot one in?</p>
                </div>
              </div>
              <div className="mt-5 flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-[#E2E8F0]">
                <input disabled placeholder="Type your message…" className="flex-1 bg-transparent border-0 outline-none focus:ring-0 font-body text-sm text-[#94A3B8] cursor-default" />
                <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center">
                  <ArrowRight className="w-4 h-4 text-white" />
                </div>
              </div>
            </motion.div>

            {/* Energy Flow */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="lg:col-span-3 relative rounded-2xl border border-white/60 bg-white/70 backdrop-blur-xl p-6 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.15)] overflow-hidden"
            >
              <p className="font-mono text-[10px] text-[#94A3B8] tracking-widest mb-1">ENERGY FLOW</p>
              <h3 className="font-heading text-base font-semibold text-[#0F172A] mb-4">Build → Peak → Cool</h3>
              <svg viewBox="0 0 200 80" className="w-full h-20">
                <defs>
                  <linearGradient id="hpEnergy" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563FF" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#14E6C9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <path d="M 0 65 Q 25 55 50 50 T 100 30 T 150 20 T 200 35 L 200 80 L 0 80 Z" fill="url(#hpEnergy)" />
                <path d="M 0 65 Q 25 55 50 50 T 100 30 T 150 20 T 200 35" fill="none" stroke="#2563FF" strokeWidth="2" />
              </svg>
              <div className="flex justify-between font-mono text-[10px] text-[#94A3B8] mt-2">
                <span>00:00</span><span>45:00</span><span>1:32</span>
              </div>
            </motion.div>

            {/* Harmonic Map */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="lg:col-span-3 relative rounded-2xl border border-white/60 bg-white/70 backdrop-blur-xl p-6 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.15)] overflow-hidden"
            >
              <p className="font-mono text-[10px] text-[#94A3B8] tracking-widest mb-1">HARMONIC MAP</p>
              <h3 className="font-heading text-base font-semibold text-[#0F172A] mb-3">Camelot path</h3>
              <div className="flex items-center justify-center">
                <svg viewBox="0 0 120 120" className="w-28 h-28">
                  <circle cx="60" cy="60" r="48" fill="none" stroke="#E2E8F0" strokeWidth="14" />
                  <circle cx="60" cy="60" r="48" fill="none" stroke="url(#hpRing)" strokeWidth="14" strokeDasharray="180 120" strokeLinecap="round" transform="rotate(-90 60 60)" />
                  <defs>
                    <linearGradient id="hpRing" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#2563FF" />
                      <stop offset="100%" stopColor="#14E6C9" />
                    </linearGradient>
                  </defs>
                  <text x="60" y="58" textAnchor="middle" className="font-mono fill-[#0F172A]" style={{fontSize:'11px', fontWeight:'bold'}}>8A → 11B</text>
                  <text x="60" y="72" textAnchor="middle" className="font-mono fill-[#64748B]" style={{fontSize:'7px'}}>4 STEPS</text>
                </svg>
              </div>
            </motion.div>

            {/* Set Insights */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="lg:col-span-3 relative rounded-2xl border border-white/60 bg-white/70 backdrop-blur-xl p-6 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.15)] overflow-hidden"
            >
              <p className="font-mono text-[10px] text-[#94A3B8] tracking-widest mb-1">SET INSIGHTS</p>
              <h3 className="font-heading text-base font-semibold text-[#0F172A] mb-3">3 signals</h3>
              <ul className="space-y-2.5">
                <li className="flex gap-2 items-start">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                  <p className="font-body text-xs text-[#475569] leading-snug">Smooth energy build between tracks 1–7</p>
                </li>
                <li className="flex gap-2 items-start">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#22D3EE] shrink-0" />
                  <p className="font-body text-xs text-[#475569] leading-snug">Good key variation — 4 distinct Camelot codes</p>
                </li>
                <li className="flex gap-2 items-start">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                  <p className="font-body text-xs text-[#475569] leading-snug">Consider a reset track around 60 min</p>
                </li>
              </ul>
            </motion.div>

            {/* Transition Suggestion */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="lg:col-span-3 relative rounded-2xl border border-white/60 bg-white/70 backdrop-blur-xl p-6 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.15)] overflow-hidden"
            >
              <p className="font-mono text-[10px] text-[#94A3B8] tracking-widest mb-1">TRANSITION</p>
              <h3 className="font-heading text-base font-semibold text-[#0F172A] mb-3">Track 3 → 4</h3>
              <div className="space-y-1 mb-3">
                <p className="font-body text-xs text-[#0F172A] truncate">Hidden Places <span className="text-[#94A3B8]">· 129 / 10A</span></p>
                <p className="font-body text-xs text-[#0F172A] truncate">Rave Signal <span className="text-[#94A3B8]">· 130 / 10B</span></p>
              </div>
              <div className="mt-3">
                <div className="flex items-center justify-between font-mono text-[10px] mb-1">
                  <span className="text-[#64748B]">Compatibility</span>
                  <span className="text-[#2563FF] font-bold">92%</span>
                </div>
                <div className="h-1.5 rounded-full bg-[#F1F5F9] overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#2563FF] to-[#14E6C9]" style={{ width: '92%' }} />
                </div>
              </div>
              <div className="flex items-center gap-1.5 mt-3">
                <span className="px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 font-mono text-[9px] text-emerald-600">PERFECT</span>
                <span className="font-mono text-[10px] text-[#94A3B8]">+1 BPM</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Camelot Wheel Section - Improved Design */}
      <section className="py-24 px-4 bg-gradient-to-b from-white to-[#F5F7FB] border-t border-[#E2E8F0] overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4 text-[#0F172A] tracking-tight">
              The <span className="gradient-text">Camelot</span> Wheel
            </h2>
            <p className="font-body text-[#64748B] max-w-2xl mx-auto">
              Master harmonic mixing like the pros. The Camelot wheel system shows you which tracks are musically compatible for seamless DJ transitions and professional-quality sets.
            </p>
          </motion.div>

          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            {/* Visual Wheel */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, rotate: -30 }}
              whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="relative w-72 h-72 sm:w-96 sm:h-96 flex-shrink-0"
            >
              {/* Outer glow */}
              <div className="absolute inset-0 rounded-full bg-[#22D3EE]/20 blur-3xl" />
              
              {/* Wheel segments */}
              <svg viewBox="0 0 200 200" className="w-full h-full relative z-10">
                <defs>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                
                {/* Background circle */}
                <circle cx="100" cy="100" r="95" fill="none" stroke="#F1F5F9" strokeWidth="40" />
                
                {/* Camelot segments */}
                {camelotKeys.map((key, i) => {
                  const angle = (i * 30 - 90) * (Math.PI / 180);
                  const nextAngle = ((i + 1) * 30 - 90) * (Math.PI / 180);
                  const outerR = 95;
                  const midR = 75;
                  const innerR = 55;
                  
                  // Outer arc (A - minor)
                  const outerX1 = 100 + outerR * Math.cos(angle);
                  const outerY1 = 100 + outerR * Math.sin(angle);
                  const outerX2 = 100 + outerR * Math.cos(nextAngle);
                  const outerY2 = 100 + outerR * Math.sin(nextAngle);
                  const midX1 = 100 + midR * Math.cos(angle);
                  const midY1 = 100 + midR * Math.sin(angle);
                  const midX2 = 100 + midR * Math.cos(nextAngle);
                  const midY2 = 100 + midR * Math.sin(nextAngle);
                  
                  // Inner arc (B - major)
                  const innerX1 = 100 + innerR * Math.cos(angle);
                  const innerY1 = 100 + innerR * Math.sin(angle);
                  const innerX2 = 100 + innerR * Math.cos(nextAngle);
                  const innerY2 = 100 + innerR * Math.sin(nextAngle);
                  
                  // Text positions
                  const textAngle = ((i * 30 + 15) - 90) * (Math.PI / 180);
                  const outerTextR = 85;
                  const innerTextR = 65;
                  
                  return (
                    <g key={key.num}>
                      {/* Outer segment (A) */}
                      <path
                        d={`M ${outerX1} ${outerY1} A ${outerR} ${outerR} 0 0 1 ${outerX2} ${outerY2} L ${midX2} ${midY2} A ${midR} ${midR} 0 0 0 ${midX1} ${midY1} Z`}
                        fill={key.color}
                        fillOpacity="0.3"
                        stroke={key.color}
                        strokeWidth="1"
                        filter="url(#glow)"
                      />
                      {/* Inner segment (B) */}
                      <path
                        d={`M ${midX1} ${midY1} A ${midR} ${midR} 0 0 1 ${midX2} ${midY2} L ${innerX2} ${innerY2} A ${innerR} ${innerR} 0 0 0 ${innerX1} ${innerY1} Z`}
                        fill={key.color}
                        fillOpacity="0.6"
                        stroke={key.color}
                        strokeWidth="1"
                        filter="url(#glow)"
                      />
                      {/* Labels */}
                      <text
                        x={100 + outerTextR * Math.cos(textAngle)}
                        y={100 + outerTextR * Math.sin(textAngle)}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="font-mono text-[8px] fill-white font-bold"
                      >
                        {key.num}A
                      </text>
                      <text
                        x={100 + innerTextR * Math.cos(textAngle)}
                        y={100 + innerTextR * Math.sin(textAngle)}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="font-mono text-[8px] fill-white font-bold"
                      >
                        {key.num}B
                      </text>
                    </g>
                  );
                })}
                
                {/* Center */}
                <circle cx="100" cy="100" r="40" fill="#FFFFFF" stroke="#2563FF" strokeWidth="2" />
                <text x="100" y="95" textAnchor="middle" className="font-logo text-[10px] fill-[#2563FF] font-bold">CAMELOT</text>
                <text x="100" y="108" textAnchor="middle" className="font-mono text-[6px] fill-[#94A3B8]">WHEEL</text>
              </svg>
            </motion.div>

            {/* Info Cards */}
            <div className="flex-1 space-y-6">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="p-6 rounded-xl border border-[#E2E8F0] bg-white shadow-sm"
              >
                <h3 className="font-heading text-lg text-[#0F172A] mb-2 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-green-500" />
                  Perfect Match
                </h3>
                <p className="font-body text-[#64748B] text-sm">
                  Mix tracks with the <span className="text-[#0F172A]">same Camelot code</span> (e.g., 8A → 8A) for a flawless harmonic blend.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="p-6 rounded-xl border border-[#E2E8F0] bg-white shadow-sm"
              >
                <h3 className="font-heading text-lg text-[#0F172A] mb-2 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-yellow-500" />
                  Energy Shift
                </h3>
                <p className="font-body text-[#64748B] text-sm">
                  Move <span className="text-[#0F172A]">+1 or -1</span> on the wheel (e.g., 8A → 9A or 7A) for smooth energy transitions.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="p-6 rounded-xl border border-[#E2E8F0] bg-white shadow-sm"
              >
                <h3 className="font-heading text-lg text-[#0F172A] mb-2 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-blue-500" />
                  Mode Switch
                </h3>
                <p className="font-body text-[#64748B] text-sm">
                  Switch between <span className="text-[#0F172A]">A (minor) and B (major)</span> with the same number for a mood change.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Desktop Analyzer Promo ─────────────────────────────────────
          Two-column layout: pitch on the left, real product screenshot
          on the right with floating BPM + Camelot wheel accents.        */}
      <section
        className="relative py-24 px-4 border-t border-[#E2E8F0] bg-gradient-to-b from-[#F5F7FB] to-white overflow-hidden"
        data-testid="desktop-analyzer-promo"
      >
        {/* Ambient background glow */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[500px] h-[500px] bg-[#2563FF]/8 rounded-full blur-3xl" />
          <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[400px] h-[400px] bg-[#8B5CF6]/8 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* ── Left: pitch ── */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
          >
            {/* Eyebrow */}
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#2563FF]/10 border border-[#2563FF]/20 rounded-full mb-6">
              <Monitor className="w-3.5 h-3.5 text-[#2563FF]" />
              <span className="font-mono text-[11px] uppercase tracking-wider text-[#2563FF]">
                Native desktop app
              </span>
            </div>

            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-5 text-[#0F172A] tracking-tight leading-tight">
              Analyze your library{' '}
              <span className="gradient-text">on your machine</span>
            </h2>

            <p className="font-body text-[#64748B] text-base sm:text-lg mb-8 leading-relaxed">
              CRATELAB Track Analyser runs entirely on your Mac or Windows machine.
              Point it at a folder of tracks and get instant BPM, Camelot key,
              energy score, and section-tinted waveforms — no upload wait, no
              server queue. Everything syncs back to your cloud library.
            </p>

            {/* Feature bullets */}
            <ul className="space-y-3 mb-8">
              {[
                { icon: Music,      text: 'Instant BPM & Camelot key detection' },
                { icon: Sparkles,   text: 'Energy scoring & auto-waveforms' },
                { icon: Cloud,      text: 'Two-way sync with your CRATELAB library' },
                { icon: Zap,        text: 'Runs locally — zero upload latency' },
              ].map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-center gap-3">
                  <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-white border border-[#E2E8F0] flex items-center justify-center shadow-sm">
                    <Icon className="w-4 h-4 text-[#2563FF]" />
                  </span>
                  <span className="font-body text-[#334155]">{text}</span>
                </li>
              ))}
            </ul>

            {/* Pricing hint */}
            <div className="flex items-center gap-2 mb-6 font-mono text-xs text-[#64748B]">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span>Free download · Pro subscription unlocks analysis</span>
            </div>

            {/* CTA — auth-aware */}
            <div className="flex flex-col sm:flex-row gap-3">
              {isAuthenticated ? (
                <Link to="/profile" data-testid="analyzer-cta-download-btn">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    Download Analyzer
                  </motion.button>
                </Link>
              ) : (
                <>
                  <Link to="/login" data-testid="analyzer-cta-login-btn">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="btn-primary inline-flex items-center gap-2"
                    >
                      Log in to start using
                      <ArrowRight className="w-5 h-5" />
                    </motion.button>
                  </Link>
                  <Link to="/signup" data-testid="analyzer-cta-signup-btn">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="btn-secondary inline-flex items-center gap-2"
                    >
                      Create account
                    </motion.button>
                  </Link>
                </>
              )}
            </div>
          </motion.div>

          {/* ── Right: screenshot with floating accents ── */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative"
          >
            {/* Soft halo behind the screenshot */}
            <div className="absolute -inset-8 bg-gradient-to-br from-[#2563FF]/20 via-transparent to-[#8B5CF6]/20 rounded-3xl blur-2xl" aria-hidden="true" />

            {/* Screenshot frame */}
            <div className="relative rounded-xl overflow-hidden border border-[#E2E8F0] shadow-[0_25px_80px_-15px_rgba(15,23,42,0.25)] bg-white">
              {/* Fake OS window chrome */}
              <div className="flex items-center gap-1.5 px-3 py-2 bg-[#F1F5F9] border-b border-[#E2E8F0]">
                <span className="w-3 h-3 rounded-full bg-[#FF5F57]" />
                <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
                <span className="w-3 h-3 rounded-full bg-[#28C840]" />
                <span className="ml-3 font-mono text-[10px] text-[#64748B] uppercase tracking-wider">
                  Cratelab Track Analyser
                </span>
              </div>
              <img
                src="/desktop-analyzer-screenshot.png"
                alt="CRATELAB Track Analyser desktop app showing track library with BPM and Camelot key detection"
                className="w-full h-auto block"
                loading="lazy"
                data-testid="analyzer-screenshot"
              />
            </div>

            {/* Floating BPM badge — top-left */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, rotate: -8 }}
              whileInView={{ opacity: 1, scale: 1, rotate: -6 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, type: 'spring', stiffness: 120 }}
              className="hidden sm:flex absolute -top-6 -left-8 lg:-left-10 flex-col items-center px-5 py-3 bg-white rounded-2xl border border-[#E2E8F0] shadow-xl"
              aria-hidden="true"
            >
              <span className="font-mono text-[10px] text-[#64748B] uppercase tracking-wider">
                BPM
              </span>
              <span className="font-heading text-3xl font-bold text-[#0F172A] leading-none mt-0.5">
                129
              </span>
              <span className="font-mono text-[9px] text-[#2563FF] mt-1">
                DETECTED
              </span>
            </motion.div>

            {/* Floating Camelot wheel — bottom-right */}
            <motion.div
              initial={{ opacity: 0, scale: 0.7, rotate: 12 }}
              whileInView={{ opacity: 1, scale: 1, rotate: 8 }}
              viewport={{ once: true }}
              transition={{ delay: 0.55, type: 'spring', stiffness: 120 }}
              className="hidden sm:block absolute -bottom-8 -right-6 lg:-right-10 w-32 h-32 lg:w-40 lg:h-40 bg-white rounded-full border border-[#E2E8F0] shadow-xl p-3"
              aria-hidden="true"
            >
              <svg viewBox="0 0 200 200" className="w-full h-full">
                {/* Simple 12-key Camelot wheel */}
                {Array.from({ length: 12 }).map((_, i) => {
                  const angle = (i * 30 - 90) * (Math.PI / 180);
                  const nextAngle = ((i + 1) * 30 - 90) * (Math.PI / 180);
                  const outerR = 95;
                  const innerR = 50;
                  const outerX1 = 100 + outerR * Math.cos(angle);
                  const outerY1 = 100 + outerR * Math.sin(angle);
                  const outerX2 = 100 + outerR * Math.cos(nextAngle);
                  const outerY2 = 100 + outerR * Math.sin(nextAngle);
                  const innerX1 = 100 + innerR * Math.cos(angle);
                  const innerY1 = 100 + innerR * Math.sin(angle);
                  const innerX2 = 100 + innerR * Math.cos(nextAngle);
                  const innerY2 = 100 + innerR * Math.sin(nextAngle);
                  // Highlight 6A (index 5) to match the screenshot's key
                  const isActive = i === 5;
                  const fill = isActive ? '#2563FF' : (i % 2 === 0 ? '#DBEAFE' : '#EEF2FF');
                  return (
                    <g key={i}>
                      <path
                        d={`M ${outerX1} ${outerY1} A ${outerR} ${outerR} 0 0 1 ${outerX2} ${outerY2} L ${innerX2} ${innerY2} A ${innerR} ${innerR} 0 0 0 ${innerX1} ${innerY1} Z`}
                        fill={fill}
                        stroke="white"
                        strokeWidth="1.5"
                      />
                      <text
                        x={100 + ((outerR + innerR) / 2) * Math.cos(angle + (Math.PI / 12))}
                        y={100 + ((outerR + innerR) / 2) * Math.sin(angle + (Math.PI / 12)) + 3}
                        textAnchor="middle"
                        fontSize="10"
                        fontWeight="700"
                        fill={isActive ? 'white' : '#475569'}
                        style={{ fontFamily: 'monospace' }}
                      >
                        {i + 1}A
                      </text>
                    </g>
                  );
                })}
                {/* Centre disc */}
                <circle cx="100" cy="100" r="42" fill="white" stroke="#E2E8F0" strokeWidth="2" />
                <text x="100" y="96" textAnchor="middle" fontSize="10" fontWeight="700" fill="#64748B" style={{ fontFamily: 'monospace', letterSpacing: '0.05em' }}>
                  KEY
                </text>
                <text x="100" y="115" textAnchor="middle" fontSize="18" fontWeight="800" fill="#2563FF" style={{ fontFamily: 'monospace' }}>
                  6A
                </text>
              </svg>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 border-t border-[#E2E8F0]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6 text-[#0F172A] tracking-tight">
              Ready to <span className="gradient-text">build your set?</span>
            </h2>
            <p className="font-body text-[#64748B] mb-8 max-w-xl mx-auto">
              <span className="hidden sm:inline">Join DJs worldwide using AI-powered track analysis. Upload your tracks and let our intelligent DJ software suggest the perfect mixing order based on Camelot wheel compatibility, BPM matching, and energy flow.</span>
              <span className="sm:hidden">Create your account, upload your tracks, and let our AI DJ software build your perfect playlists with harmonic mixing.</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup" data-testid="cta-signup-btn">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Get Started
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </Link>
              <Link to="/login" data-testid="cta-login-btn" className="sm:hidden">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="btn-secondary inline-flex items-center gap-2"
                >
                  Already have an account? Sign In
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-[#E2E8F0] bg-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <BrandLogo size="sm" className="opacity-80" />
          <p className="font-mono text-xs text-[#94A3B8]">
            © 2026 CRATELAB. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
